import { FC, ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import {
    DataTable,
    DataTableColumn,
    DataTableEntityCell,
    DataTableFilter,
    DataTableFilterSpec,
    SearchInput,
} from "../../../design-system";
import { capitalize } from "../../../common/String";
import {
    AnalysisType,
    canDo,
    getAnalysisLabel,
    getProjectTypeLabel,
    ProjectAction,
    ProjectSummary,
    ProjectType,
} from "../../../types/ProjectTypes";
import { SpendCategory } from "../../../types/VendorTypes";
import { useUsers } from "../../../common/useUsers";
import { ownerFilter } from "../../../common/components/OwnerFilter";
import { CategoryTree } from "../../../common/components/CategoryPicker";
import { reassignProjectOwner } from "../../../common/api/ProjectClient";
import { getRelativeTime } from "../../date/DateOp";
import { OwnerCell } from "../../../common/components/OwnerCell";
import { useOrgCategories } from "../../../common/useOrgCategories";
import { categoryLabel } from "../../../common/components/CategoryPath";
import { ProjectCategoryWidget } from "./ProjectCategoryWidget";
import { projectLogoFallback } from "./VendorLogo";
import { ProjectStatusCell, statusPresentation } from "./ProjectStatusCell";
import { AnalysisProgressCell } from "./AnalysisProgressCell";
import styles from "./ProjectsTable.module.less";

interface ProjectsTableProps {
    projects: ProjectSummary[];
    /** Called after a row changes (owner reassigned, category set) so the list reloads. */
    onUpdate: () => void;
    /** Pre-defined filters of the projects, in the table's toolbar. */
    filters?: DataTableFilter<ProjectSummary>[];
    defaultFilter?: string;
    onFilterChange?: (key: string) => void;
    emptyText?: ReactNode;
}

const projectPath = (project: ProjectSummary) => `/project/${project.id}`;

// The Analysis column's sort key; a closed project without a report sorts as empty.
const analysisLabel = (project: ProjectSummary) => {
    if (!project.latestAnalysis) return "Draft";
    if (project.latestAnalysis.type === AnalysisType.COMPLETE) return "";
    return getAnalysisLabel(project.latestAnalysis);
};

// The Status filter's choices: the pipeline stage, which deals and RFx share
// even though they name it differently ("Ready to sign" and "Select vendor"
// are both Ready; "Signed" and "Complete" close the pipeline).
const STATUS_OPTIONS = [
    "Not started",
    "In progress",
    "Ready",
    "Complete / Signed",
    "Cancelled",
].map((value) => ({ value, label: value }));

const statusStage = (project: ProjectSummary): string | undefined => {
    const status = statusPresentation(project);
    if (!status) return undefined;
    if (status.label === "Cancelled") return "Cancelled";
    return STATUS_OPTIONS[status.stage].value;
};

// The Type filter's choices.
const TYPE_OPTIONS = [ProjectType.DEAL, ProjectType.RFX].map((type) => ({
    value: type,
    label: capitalize(getProjectTypeLabel(type)),
}));

// A category filter value is the path as JSON, since labels repeat across branches.
const categoryValue = (path: string[]) => JSON.stringify(path);
const categoryPathOf = (value: string): string[] => {
    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

/** The Category filter's editor: a search box over the org's taxonomy tree. */
const CategoryFilterEditor: FC<{
    categories: SpendCategory[];
    value?: string;
    onChange: (value: string | undefined) => void;
}> = ({ categories, value, onChange }) => {
    const [query, setQuery] = useState("");
    return (
        <div>
            <div className={styles.editorSearch}>
                <SearchInput
                    autoFocus
                    placeholder="Search categories…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />
            </div>
            <CategoryTree
                categories={categories}
                value={value ? categoryPathOf(value) : undefined}
                onSelect={(path) => onChange(categoryValue(path))}
                query={query}
            />
        </div>
    );
};

/** The procurement hub's project list. */
export const ProjectsTable: FC<ProjectsTableProps> = ({
    projects,
    onUpdate,
    filters,
    defaultFilter,
    onFilterChange,
    emptyText,
}) => {
    // Only orgs with their own taxonomy have a Category column.
    const categories = useOrgCategories();
    // For the Owner filter's chip to name the picked user.
    const users = useUsers();

    const categoryFilter: DataTableFilterSpec<ProjectSummary> | undefined = categories
        ? {
              type: "pick",
              render: (value, onChange) => (
                  <CategoryFilterEditor categories={categories} value={value} onChange={onChange} />
              ),
              label: (value) => categoryLabel(categoryPathOf(value)),
              // A branch keeps the projects filed under any of its leaves.
              match: (project, value) => {
                  const path = categoryPathOf(value);
                  return (
                      !!project.categoryPath &&
                      path.every((label, i) => project.categoryPath?.[i] === label)
                  );
              },
          }
        : undefined;

    const categoryColumn: DataTableColumn<ProjectSummary>[] = categories
        ? [
              {
                  key: "category",
                  title: "Category",
                  sort: "text",
                  sortValue: (project) =>
                      project.categoryPath ? categoryLabel(project.categoryPath) : undefined,
                  filter: categoryFilter,
                  render: (project) => (
                      <ProjectCategoryWidget
                          projectId={project.id}
                          categoryPath={project.categoryPath}
                          categories={categories}
                          canEdit={canDo(project, ProjectAction.EDIT)}
                          onUpdate={onUpdate}
                      />
                  ),
              },
          ]
        : [];

    const columns: DataTableColumn<ProjectSummary>[] = [
        {
            key: "project",
            title: "Project",
            width: "20%",
            sort: "text",
            sortValue: (project) => project.name,
            render: (project) => {
                const vendorName = project.vendorName || project.vendorKey || "";
                // Same stand-in as the project page header's logo.
                const fallback = projectLogoFallback(vendorName, project.name);
                return (
                    <DataTableEntityCell
                        imageSrc={project.vendorHero}
                        imageFallback={fallback.letter}
                        imageFallbackColor={fallback.color}
                        name={
                            <Link to={projectPath(project)}>
                                {project.name || "Unnamed project"}
                            </Link>
                        }
                        detail={vendorName || capitalize(getProjectTypeLabel(project.type))}
                    />
                );
            },
        },
        {
            // Mostly here for its filter: the entity cell already says the
            // type under a project without a vendor, so the column starts hidden.
            key: "type",
            title: "Type",
            defaultHidden: true,
            sort: "text",
            sortValue: (project) => project.type,
            filter: { type: "enum", options: TYPE_OPTIONS, value: (project) => project.type },
            render: (project) => capitalize(getProjectTypeLabel(project.type)),
        },
        ...categoryColumn,
        {
            key: "owner",
            title: "Owner",
            sort: "text",
            sortValue: (project) => project.ownerName,
            filter: ownerFilter((project) => project.ownerId, users),
            render: (project) => (
                <OwnerCell
                    ownerId={project.ownerId}
                    ownerName={project.ownerName}
                    canEdit={canDo(project, ProjectAction.REASSIGN)}
                    onSave={async (newOwnerId) => {
                        await reassignProjectOwner(project.id, newOwnerId);
                    }}
                    onUpdate={onUpdate}
                />
            ),
        },
        {
            key: "status",
            title: "Status",
            // Pipeline order: not started, in progress, ready, complete.
            sort: "number",
            sortValue: (project) => statusPresentation(project)?.stage,
            filter: { type: "enum", options: STATUS_OPTIONS, value: statusStage },
            render: (project) => <ProjectStatusCell project={project} />,
        },
        {
            key: "analysis",
            title: "Analysis",
            sort: "text",
            sortValue: (project) => analysisLabel(project),
            render: (project) => <AnalysisProgressCell project={project} />,
        },
        {
            key: "updated",
            title: "Updated",
            align: "right",
            sort: "date",
            sortValue: (project) => project.lastUpdateDate,
            render: (project) => getRelativeTime(project.lastUpdateDate),
        },
    ];

    return (
        <DataTable<ProjectSummary>
            columns={columns}
            rows={projects}
            rowKey={(project) => project.id}
            filters={filters}
            defaultFilter={defaultFilter}
            onFilterChange={onFilterChange}
            searchText={(project) => [project.name, project.vendorName, project.vendorKey]}
            searchPlaceholder="Search projects"
            emptyText={emptyText}
            storageKey="projects"
        />
    );
};
